import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileSearch, Layers, RefreshCw, Trash2, Unlink } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { EmptyState, HeaderCount, PageHeader, SectionCard } from "../components/ui";
import { useOfficeDocBatches, useOfficeDocScanMutations, useOfficeDocScans } from "../hooks";
import { fmtDateTime } from "../utils";
import type { OfficeDocBatch, OfficeDocFacts, OfficeDocProposal, OfficeDocScan } from "../types";

const FILTERS = [
  { value: "", label: "Wszystkie" },
  { value: "matched", label: "Rozpoznane" },
  { value: "unmatched", label: "Nierozpoznane" },
] as const;

/** Kolor „pewności" propozycji: 100 = numer wniosku wprost w dokumencie, niżej —
 *  dopasowanie po geometrii albo po treści, które trzeba jeszcze potwierdzić okiem. */
function scoreColor(score: number) {
  if (score >= 90) return "green" as const;
  if (score >= 55) return "yellow" as const;
  return "gray" as const;
}

function StatusBadge({ status, count }: { status: string; count: number }) {
  if (status === "pending") return <Badge color="gray">w kolejce</Badge>;
  if (status === "running") return <Badge color="blue">analiza…</Badge>;
  if (status === "failed" && !count) return <Badge color="red">błąd</Badge>;
  return count ? (
    <Badge color="green">{count} propozycji</Badge>
  ) : (
    <Badge color="orange">nie rozpoznano</Badge>
  );
}

function ProposalRow({ proposal }: { proposal: OfficeDocProposal }) {
  return (
    <li className="flex items-start gap-3 py-1.5 border-t border-[var(--color-border)] first:border-t-0">
      <Badge color={scoreColor(proposal.score)} className="mt-0.5 w-12 justify-center">
        {proposal.score}
      </Badge>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={proposal.href} className="font-mono font-bold hover:underline">
            {proposal.number}
          </Link>
          <span className="text-[var(--color-text-secondary)]">{proposal.kind_label}</span>
          {proposal.label && <span className="truncate">· {proposal.label}</span>}
          <Badge color="gray">{proposal.status_label}</Badge>
        </div>
        <div className="text-[0.7rem] text-[var(--color-text-secondary)]">
          {proposal.reasons.join(" · ")}
        </div>
      </div>
    </li>
  );
}

/** Na czym system się opierał — pokazywane przy KAŻDYM wyniku, także (a zwłaszcza)
 *  przy „nie rozpoznano": widać wtedy, czy dokument w ogóle dał się odczytać. */
function FactsLine({ facts }: { facts: OfficeDocFacts | undefined }) {
  const f = facts ?? {};
  const bits: string[] = [];
  if (f.office_name) bits.push(f.office_name);
  if (f.case_sign) bits.push(`znak: ${f.case_sign}`);
  if (f.county) bits.push(`powiat ${f.county}`);
  if (f.commune) bits.push(`gmina ${f.commune}`);
  if (f.parcels?.length) bits.push(`działek: ${f.parcels.length}`);
  if (f.srid) bits.push(`EPSG:${f.srid}${f.axes_swapped ? " (osie zamienione)" : ""}`);
  if (f.numbers?.length) bits.push(`numery: ${f.numbers.join(", ")}`);
  if (!bits.length && !f.summary) return null;
  return (
    <div className="text-[0.7rem] text-[var(--color-text-secondary)] mt-1">
      {f.summary && <div className="italic">{f.summary}</div>}
      {bits.join(" · ")}
    </div>
  );
}

/** Wynik: propozycje albo uczciwe „nie wykryto". */
function Result({
  status,
  proposals,
  error,
}: {
  status: string;
  proposals: OfficeDocProposal[];
  error?: string;
}) {
  if (status === "pending" || status === "running") {
    return (
      <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
        <Spinner size="sm" /> Rozpoznaję…
      </div>
    );
  }
  if (proposals.length) {
    return (
      <ul>
        {proposals.map((p) => (
          <ProposalRow key={`${p.kind}-${p.order_id}`} proposal={p} />
        ))}
      </ul>
    );
  }
  return (
    <div className="text-xs text-amber-700">
      Nie wykryto zamówienia, którego mógłby dotyczyć ten dokument — przypisz sprawę ręcznie.
      {error ? ` (${error})` : ""}
    </div>
  );
}

function ScanHeader({
  scan,
  selectable,
  selected,
  onSelect,
  actions,
}: {
  scan: OfficeDocScan;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  actions: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {selectable && (
            <input type="checkbox" checked={!!selected} onChange={onSelect} title="Zaznacz do paczki" />
          )}
          <FileSearch size={14} className="text-[var(--color-text-secondary)]" />
          {scan.file?.url ? (
            <a
              href={scan.file.url}
              target="_blank"
              rel="noreferrer"
              className="font-medium hover:underline truncate"
            >
              {scan.filename}
            </a>
          ) : (
            <span className="font-medium truncate">{scan.filename}</span>
          )}
          <StatusBadge status={scan.status} count={scan.proposals.length} />
          {scan.doc_kind_label && <Badge color="purple">{scan.doc_kind_label}</Badge>}
        </div>
        <div className="text-[0.7rem] text-[var(--color-text-secondary)]">
          {fmtDateTime(scan.created_at)}
          {scan.uploaded_by_name ? ` · ${scan.uploaded_by_name}` : ""}
        </div>
        <FactsLine facts={scan.facts} />
      </div>
      <div className="flex items-center gap-1 shrink-0">{actions}</div>
    </div>
  );
}

/** Preanaliza dokumentów przychodzących z urzędów. Urzędy prawie nigdy nie oznaczają
 *  odpowiedzi naszym numerem wniosku, więc przy kilkunastu sprawach naraz trzeba było
 *  otwierać plik po pliku. Tu wrzuca się je hurtem i dostaje linki do zamówień —
 *  a to, co przyszło jednym mailem, można wrzucić (albo zebrać) jako jedną paczkę. */
export function OfficeDocsPage() {
  const me = useAuthStore((s) => s.user);
  const { data: scans, isLoading } = useOfficeDocScans();
  const { data: batches } = useOfficeDocBatches();
  const { upload, reanalyze, remove, group, ungroup } = useOfficeDocScanMutations();

  const [filter, setFilter] = useState<(typeof FILTERS)[number]["value"]>("");
  const [mineOnly, setMineOnly] = useState(false);
  const [asBatch, setAsBatch] = useState(false);
  const [label, setLabel] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  const toggle = (id: string) =>
    setSelected((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const loose = useMemo(() => {
    let rows = (scans ?? []).filter((s) => !s.batch);
    if (filter === "matched") rows = rows.filter((s) => s.proposals.length > 0);
    if (filter === "unmatched") {
      rows = rows.filter((s) => !s.proposals.length && (s.status === "done" || s.status === "failed"));
    }
    if (mineOnly && me?.id) rows = rows.filter((s) => String(s.uploaded_by) === String(me.id));
    return rows;
  }, [scans, filter, mineOnly, me?.id]);

  const visibleBatches = useMemo(() => {
    let rows = batches ?? [];
    if (filter === "matched") rows = rows.filter((b) => b.proposals.length > 0);
    if (filter === "unmatched") rows = rows.filter((b) => !b.proposals.length && b.status !== "running");
    if (mineOnly && me?.id) rows = rows.filter((b) => String(b.created_by) === String(me.id));
    return rows;
  }, [batches, filter, mineOnly, me?.id]);

  const pending =
    (scans ?? []).filter((s) => s.status === "pending" || s.status === "running").length;

  const scanActions = (scan: OfficeDocScan, inBatch: boolean) => (
    <>
      {inBatch && (
        <Button
          size="sm"
          variant="secondary"
          title="Wyjmij z paczki"
          disabled={ungroup.isPending}
          onClick={() => ungroup.mutate(scan.id)}
        >
          <Unlink size={13} />
        </Button>
      )}
      <Button
        size="sm"
        variant="secondary"
        title="Analizuj ponownie"
        disabled={reanalyze.isPending}
        onClick={() => reanalyze.mutate(scan.id)}
      >
        <RefreshCw size={13} />
      </Button>
      <Button
        size="sm"
        variant="secondary"
        title="Usuń z listy"
        disabled={remove.isPending}
        onClick={() => {
          if (window.confirm(`Usunąć „${scan.filename}" z listy?`)) remove.mutate(scan.id);
        }}
      >
        <Trash2 size={13} />
      </Button>
    </>
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Dokumenty z urzędów"
        subtitle="Wrzuć, co przyszło z urzędu — system podpowie, którego zamówienia to dotyczy (wypisy EGiB, mapa zasadnicza, MPZP)"
        actions={<HeaderCount count={pending} label="w analizie" />}
      />

      <SectionCard title="Wrzuć dokumenty">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" checked={asBatch} onChange={() => setAsBatch((v) => !v)} />
            To jedna sprawa (jedna paczka)
          </label>
          {asBatch && (
            <input
              className="input-base !py-1 text-xs flex-1 min-w-[220px]"
              placeholder="Opis paczki, np. „mail ze starostwa 13.08 — mapa + licencja”"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          )}
        </div>
        <FileDropZone
          multiple
          disabled={upload.isPending}
          label={
            asBatch
              ? "Przeciągnij pliki JEDNEJ sprawy (np. mapa DXF + licencja PDF)"
              : "Przeciągnij dokumenty z urzędu (PDF, DXF, ZIP) albo kliknij, by wybrać"
          }
          onSelect={(files) => {
            upload.mutate({ files, asBatch, label });
            setLabel("");
          }}
        />
        <p className="mt-2 text-[0.7rem] text-[var(--color-text-secondary)]">
          Główna droga rozpoznania to <strong>działki</strong> — urząd rzadko podaje numer naszego
          wniosku, ale prawie zawsze pisze, czego dotyczy sprawa. Poza tym: numer wniosku (jeśli
          jest), geometria pliku DXF (mapa zasadnicza — układ PL-2000 wychodzi z samych
          współrzędnych, także z pliku podpisanego elektronicznie), adresat i znak sprawy urzędu
          znany z wcześniejszych dokumentów. Paczka dostaje jeden wspólny wynik: fakty ze wszystkich
          plików są łączone, a zamówienie potwierdzone kilkoma plikami idzie na górę. Wynik to
          podpowiedź — nic nie jest nigdzie podpinane.
        </p>
        {upload.isError && (
          <p className="mt-2 text-xs text-red-600">Nie udało się wgrać plików.</p>
        )}
      </SectionCard>

      {visibleBatches.map((batch) => (
        <BatchCard
          key={batch.id}
          batch={batch}
          actions={scanActions}
        />
      ))}

      <SectionCard
        title={`Pojedyncze dokumenty (${loose.length})`}
        action={
          <div className="flex items-center gap-2">
            {selected.length >= 2 && (
              <Button
                size="sm"
                disabled={group.isPending}
                onClick={() => {
                  group.mutate(
                    { ids: selected, label: "" },
                    { onSuccess: () => setSelected([]) },
                  );
                }}
              >
                <Layers size={13} className="mr-1" /> Połącz w paczkę ({selected.length})
              </Button>
            )}
            <label className="flex items-center gap-1 text-xs cursor-pointer">
              <input type="checkbox" checked={mineOnly} onChange={() => setMineOnly((v) => !v)} />
              moje
            </label>
            <select
              className="input-base !py-0.5 text-xs"
              value={filter}
              onChange={(e) => setFilter(e.target.value as typeof filter)}
            >
              {FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
        }
        bodyClassName="p-0"
      >
        {isLoading ? (
          <div className="py-8 flex justify-center"><Spinner /></div>
        ) : !loose.length ? (
          <EmptyState>Brak dokumentów — wrzuć pliki, żeby sprawdzić, czego dotyczą.</EmptyState>
        ) : (
          <ul>
            {loose.map((scan) => (
              <li key={scan.id} className="px-4 py-3 border-b border-[var(--color-border)] last:border-b-0">
                <ScanHeader
                  scan={scan}
                  selectable
                  selected={selected.includes(scan.id)}
                  onSelect={() => toggle(scan.id)}
                  actions={scanActions(scan, false)}
                />
                <div className="mt-2 pl-6">
                  <Result status={scan.status} proposals={scan.proposals} error={scan.error} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

/** Paczka: na górze wspólny wynik (to jest odpowiedź na pytanie „czego dotyczy ten
 *  mail"), a pod nim pliki z ich własnymi rozpoznaniami — żeby było widać, co z czego. */
function BatchCard({
  batch,
  actions,
}: {
  batch: OfficeDocBatch;
  actions: (scan: OfficeDocScan, inBatch: boolean) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <SectionCard
      title={
        <span className="flex items-center gap-2 normal-case tracking-normal">
          <Layers size={13} />
          {batch.label || `Paczka (${batch.scans.length} plików)`}
        </span>
      }
      action={
        <div className="flex items-center gap-2">
          <StatusBadge status={batch.status} count={batch.proposals.length} />
          <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)}>
            {open ? "Ukryj pliki" : `Pliki (${batch.scans.length})`}
          </Button>
        </div>
      }
    >
      <div className="text-[0.7rem] text-[var(--color-text-secondary)]">
        {batch.facts?.files?.join(" + ")}
      </div>
      <FactsLine facts={batch.facts} />
      <div className="mt-2">
        <Result status={batch.status} proposals={batch.proposals} />
      </div>
      {open && (
        <ul className="mt-3 border-t border-[var(--color-border)]">
          {batch.scans.map((scan) => (
            <li key={scan.id} className="py-3 border-b border-[var(--color-border)] last:border-b-0">
              <ScanHeader scan={scan} actions={actions(scan, true)} />
              <div className="mt-2 pl-6">
                <Result status={scan.status} proposals={scan.proposals} error={scan.error} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
